import {
  DotsVerticalIcon,
  Pencil2Icon,
  PersonIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { Label } from '@radix-ui/react-label';
import { Card, Checkbox, Flex, IconButton, Text } from '@radix-ui/themes';
import { CommonHitterAvatar } from 'components/common/hitters/avatar';
import { HitterRosterStatusIcon } from 'components/common/hitters/hitter-roster-status-icon';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { CommonMenu } from 'components/common/menu';
import { HitterLibraryTeamLogo } from 'components/sections/hitter-library/team-logo';
import { HittersContext } from 'contexts/hitters.context';
import { CheckedContext } from 'contexts/layout/checked.context';
import { CrudAction } from 'enums/tables';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IHitter } from 'lib_ts/interfaces/i-hitter';
import { CSSProperties, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { paths } from 'routes/paths';
import './card.scss';

export const HitterLibraryHitterCard = (props: { hitter: IHitter }) => {
  const navigate = useNavigate();

  const { hitter } = props;

  const { confirmDeleteMany, openCrudDialog } = useContext(HittersContext);
  const { getChecked, setChecked } = useContext(CheckedContext);

  const rgb = StringHelper.hexToRgb(hitter._mx_mlb_team_details?.colorHex);

  const checkboxID = `cb-${props.hitter._id}`;

  const handleOpenProfile = () => {
    navigate(paths.hitterLibrary.profile(props.hitter._id));
  };

  return (
    <Card
      key={hitter._id}
      size="2"
      className="HitterLibraryHitter cursor-pointer animate-background"
      // team-color accent consumed by the hover tint in card.scss
      style={{ '--hitter-card-accent': rgb.join(', ') } as CSSProperties}
      onClick={() => handleOpenProfile()}
    >
      <Flex gap="2" justify="between" align="center">
        <Label
          htmlFor={checkboxID}
          className="CheckboxWrapperLabel"
          // avoid click from opening the player
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            id={checkboxID}
            checked={getChecked(props.hitter._id)}
            // avoid click from opening the player
            onClick={(e) => e.stopPropagation()}
            onCheckedChange={(v) => setChecked([props.hitter._id], v !== false)}
          />
        </Label>

        <CommonHitterAvatar size="4" hitter={hitter} />

        <Flex direction="column" gap="1" flexGrow="1" overflow="hidden">
          <Text size="3" title={hitter.name} truncate>
            {hitter.name}
          </Text>

          <Flex gap="2">
            <HitterLibraryTeamLogo size="20px" hitter={hitter} />

            <Text color={RADIX.COLOR.SECONDARY} title={hitter.level} truncate>
              {hitter.level}
            </Text>
          </Flex>
        </Flex>

        <HitterSideBadge value={hitter.side} />

        <HitterRosterStatusIcon status={hitter.status} />

        <CommonMenu
          title="Hitter Actions"
          trigger={
            <IconButton
              className="btn-floating"
              variant={RADIX.BUTTON.VARIANT.BORDERLESS}
              color={RADIX.COLOR.NEUTRAL}
              onClick={(e) => e.stopPropagation()}
            >
              <DotsVerticalIcon />
            </IconButton>
          }
          actions={[
            {
              prefixIcon: <PersonIcon />,
              label: 'hitters.view-profile',
              onClick: () => handleOpenProfile(),
            },
            {
              prefixIcon: <Pencil2Icon />,
              label: 'common.edit',
              onClick: () =>
                openCrudDialog({
                  key: Date.now(),
                  action: CrudAction.Update,
                  models: [props.hitter],
                }),
            },
            {
              prefixIcon: <TrashIcon />,
              label: 'common.delete',
              color: RADIX.COLOR.DANGER,
              onClick: () => confirmDeleteMany([props.hitter]),
            },
          ]}
          skipSort
        />
      </Flex>
    </Card>
  );
};
